package org.chiangmeng.qfast.core.data.remote

import dev.gitlive.firebase.database.DataSnapshot
import dev.gitlive.firebase.database.DatabaseReference
import dev.gitlive.firebase.database.FirebaseDatabase
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.catch
import kotlinx.coroutines.flow.first
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.sync.Mutex
import kotlinx.coroutines.sync.withLock
import kotlinx.coroutines.withContext
import kotlin.time.Clock
import kotlinx.datetime.Instant
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime
import kotlinx.serialization.serializer
import org.chiangmeng.qfast.core.domain.models.QueueTicket
import org.chiangmeng.qfast.core.domain.models.Shop
import org.chiangmeng.qfast.core.domain.models.QueueHistoryItem
import org.chiangmeng.qfast.core.domain.ports.QueueRepository
import org.chiangmeng.qfast.database.QueueHistoryQueries

class FirebaseQueueRepositoryImpl(
    private val firebaseDatabase: FirebaseDatabase,
    private val historyQueries: QueueHistoryQueries
) : QueueRepository {

    private val nextQueueMutex = Mutex()

    override fun observeShop(shopId: String): Flow<Shop?> {
        val ref: DatabaseReference = firebaseDatabase.reference("shops/$shopId")
        return ref.valueEvents
            .map { snapshot: DataSnapshot ->
                if (!snapshot.exists) {
                    println("QFAST_DEBUG: ❌ Snapshot shops/$shopId NOT FOUND in Firebase")
                    return@map null
                }

                println("QFAST_DEBUG: 📦 Snapshot exists. Checking fields...")

                val idValue = try {
                    snapshot.child("id").value<String?>() ?: shopId
                } catch (t: Throwable) {
                    println("QFAST_DEBUG: ❌ Failed field 'id': $t")
                    shopId
                }

                val nameValue = try {
                    snapshot.child("name").value<String?>() ?: "QFast Shop"
                } catch (t: Throwable) {
                    println("QFAST_DEBUG: ❌ Failed field 'name': $t")
                    "QFast Shop"
                }

                val currentServingsMap = try {
                    val cs = snapshot.child("current_servings")
                    val a = try { cs.child("A").value<Long?>()?.toInt() ?: 0 } catch (t: Throwable) { println("QFAST_DEBUG: ❌ current_servings/A error: $t"); 0 }
                    val b = try { cs.child("B").value<Long?>()?.toInt() ?: 0 } catch (t: Throwable) { println("QFAST_DEBUG: ❌ current_servings/B error: $t"); 0 }
                    val c = try { cs.child("C").value<Long?>()?.toInt() ?: 0 } catch (t: Throwable) { println("QFAST_DEBUG: ❌ current_servings/C error: $t"); 0 }
                    val d = try { cs.child("D").value<Long?>()?.toInt() ?: 0 } catch (t: Throwable) { println("QFAST_DEBUG: ❌ current_servings/D error: $t"); 0 }
                    mapOf("A" to a, "B" to b, "C" to c, "D" to d)
                } catch (t: Throwable) {
                    println("QFAST_DEBUG: ❌ Failed field 'current_servings': $t")
                    mapOf("A" to 0, "B" to 0, "C" to 0, "D" to 0)
                }

                val lastTicketValue = try {
                    snapshot.child("last_ticket").value<Long?>()?.toInt() ?: 0
                } catch (t: Throwable) {
                    println("QFAST_DEBUG: ❌ Failed field 'last_ticket': $t")
                    0
                }

                val updatedAtValue = try {
                    snapshot.child("updated_at").value<Long?>() ?: 0L
                } catch (_: Throwable) {
                    try {
                        snapshot.child("updated_at").value<Double?>()?.toLong() ?: 0L
                    } catch (_: Throwable) {
                        try {
                            val map = snapshot.child("updated_at").value<Map<String, Long>?>()
                            if (map != null) {
                                val low = map["f3_1"] ?: map["low"] ?: 0L
                                val high = map["g3_1"] ?: map["high"] ?: 0L
                                (high shl 32) or (low and 0xFFFFFFFFL)
                            } else {
                                0L
                            }
                        } catch (_: Throwable) {
                            0L
                        }
                    }
                }

                val isActiveValue = try {
                    snapshot.child("is_active").value<Boolean?>() ?: true
                } catch (_: Throwable) {
                    try {
                        (snapshot.child("is_active").value<Long?>() ?: 1L) != 0L
                    } catch (_: Throwable) {
                        true
                    }
                }

                val categoriesMap = try {
                    val catSnap = snapshot.child("categories")
                    if (catSnap.exists) {
                        catSnap.children.associate { (it.key ?: "") to (it.value<String?>() ?: "") }.filterKeys { it.isNotBlank() }
                    } else {
                        mapOf("A" to "1-2 คน", "B" to "2-3 คน", "C" to "3-4 คน", "D" to "5 คนขึ้นไป")
                    }
                } catch (_: Throwable) {
                    mapOf("A" to "1-2 คน", "B" to "2-3 คน", "C" to "3-4 คน", "D" to "5 คนขึ้นไป")
                }

                println("QFAST_DEBUG: ✅ Map Shop complete -> name: $nameValue | active: $isActiveValue | lastTicket: $lastTicketValue")

                Shop(
                    id = idValue,
                    name = nameValue,
                    currentServings = currentServingsMap,
                    lastTicket = lastTicketValue,
                    isActive = isActiveValue,
                    updatedAt = updatedAtValue,
                    adText = try { snapshot.child("ad_text").value<String?>() } catch (_: Throwable) { null },
                    menuUrl = try { snapshot.child("menu_url").value<String?>() } catch (_: Throwable) { null },
                    openTime = try { snapshot.child("open_time").value<String?>() } catch (_: Throwable) { null },
                    closeTime = try { snapshot.child("close_time").value<String?>() } catch (_: Throwable) { null },
                    theme = try { snapshot.child("theme").value<String?>() } catch (_: Throwable) { null },
                    categories = categoriesMap
                )
            }
            .catch { e ->
                println("QFAST_DEBUG: ❌ Firebase Stream Error: $e")
                e.printStackTrace()
            }
    }

    override fun observeActiveQueues(shopId: String): Flow<List<QueueTicket>> {
        val ref: DatabaseReference = firebaseDatabase.reference("active_queues/$shopId")
        return ref.valueEvents
            .map { snapshot: DataSnapshot ->
                try {
                    snapshot.children.mapNotNull { child ->
                        val number = child.key?.toIntOrNull() ?: return@mapNotNull null
                        QueueTicket(
                            number = number,
                            category = child.child("category").value<String?>() ?: "A",
                            status = child.child("status").value<String?>() ?: "waiting",
                            time = child.child("time").value<String?>() ?: "",
                            callTime = try {
                                if (child.child("call_time").exists) {
                                    child.child("call_time").value<String?>()?.takeIf { it.isNotBlank() && it != "<null>" && it != "null" }
                                } else null
                            } catch (_: Throwable) {
                                null
                            }
                        )
                    }
                } catch (e: Exception) {
                    println("QFAST_LOG: Error mapping queues -> ${e.message}")
                    emptyList()
                }
            }
            .catch { e ->
                println("QFAST_LOG: Firebase Flow Error (observeActiveQueues) -> ${e.message}")
            }
    }

    override suspend fun nextQueue(shopId: String, category: String): Int? = nextQueueMutex.withLock {
        println("QFAST_MULTI_LOG: [NextQueue] -> shop: $shopId, cat: $category")
        val activeQueuesRef = firebaseDatabase.reference("active_queues/$shopId")
        val snapshots = activeQueuesRef.valueEvents.first().children

        // 1. ค้นหาคิว waiting ถัดไปที่มีเลขน้อยที่สุดก่อน
        val nextTicketSnapshot = snapshots
            .filter {
                it.child("status").value<String?>() == "waiting" &&
                it.child("category").value<String?>() == category
            }
            .minByOrNull { it.key?.toIntOrNull() ?: Int.MAX_VALUE } ?: return@withLock null

        val nextNumber = nextTicketSnapshot.key!!.toInt()
        val ticketTime = nextTicketSnapshot.child("time").value<String?>() ?: ""
        val localDateTime = Instant.fromEpochMilliseconds(Clock.System.now().toEpochMilliseconds()).toLocalDateTime(TimeZone.currentSystemDefault())
        val hour = localDateTime.hour.toString().padStart(2, '0')
        val minute = localDateTime.minute.toString().padStart(2, '0')
        val callTimeString = "$hour:$minute"

        // 2. บันทึกประวัติลง SQLite
        withContext(Dispatchers.Default) {
            historyQueries.insertHistory(
                shop_id = shopId,
                ticket_number = nextNumber.toLong(),
                served_at = Clock.System.now().toEpochMilliseconds()
            )
        }

        // 3. อัปเดตคิวใหม่ขึ้นสถานะ serving พร้อมกันในครั้งเดียว
        val updatedTicketMap = mapOf(
            "category" to category,
            "status" to "serving",
            "time" to ticketTime,
            "call_time" to callTimeString
        )
        activeQueuesRef.child(nextNumber.toString()).setValue(updatedTicketMap)

        // 4. อัปเดต current_servings ของร้าน
        val shopRef = firebaseDatabase.reference("shops/$shopId")
        shopRef.child("current_servings/$category").setValue(nextNumber)

        // 5. ล้างคิวที่เคย serving อยู่ก่อนหน้าเฉพาะเมื่อคิวใหม่อัปเดตสำเร็จแล้ว
        val previousServingTickets = snapshots.filter {
            it.key != nextNumber.toString() &&
            it.child("category").value<String?>() == category &&
            it.child("status").value<String?>() == "serving"
        }
        previousServingTickets.forEach { prevTicket ->
            prevTicket.key?.let { ticketKey ->
                activeQueuesRef.child(ticketKey).removeValue()
            }
        }

        println("QFAST_MULTI_LOG: [NextQueue Success] -> $category #$nextNumber")
        return@withLock nextNumber
    }

    override suspend fun issueTicket(shopId: String, category: String): Int {
        println("QFAST_MULTI_LOG: [ออกตั๋วคิว] -> มีลูกค้ากดขอรับตั๋วคิวร้าน $shopId ในหมวด $category")
        val shopRef = firebaseDatabase.reference("shops/$shopId")

        val newLastTicketSnapshot = shopRef.child("last_ticket").runTransaction(serializer<Long?>()) { current ->
            (current ?: 0L) + 1L
        }
        val newLastTicket = newLastTicketSnapshot.value<Long?>()?.toInt() ?: 0

        val localDateTime = Instant.fromEpochMilliseconds(Clock.System.now().toEpochMilliseconds()).toLocalDateTime(TimeZone.currentSystemDefault())
        val hour = localDateTime.hour.toString().padStart(2, '0')
        val minute = localDateTime.minute.toString().padStart(2, '0')
        val currentTimeString = "$hour:$minute"

        val ticketMap = mapOf(
            "category" to category,
            "status" to "waiting",
            "time" to currentTimeString
        )
        firebaseDatabase.reference("active_queues/$shopId/$newLastTicket").setValue(ticketMap)
        println("QFAST_MULTI_LOG: 🎉 [ออกตั๋วสำเร็จ] -> ออกตั๋วใบใหม่หมายเลข #$newLastTicket (หมวด $category) สำเร็จ")
        return newLastTicket
    }

    override suspend fun resetQueue(shopId: String) {
        println("QFAST_MULTI_LOG: ⚠️ [ระบบสิ้นวัน] -> กำลังสั่งล้างข้อมูลคิวของร้าน $shopId กลับเป็นศูนย์...")
        val shopRef = firebaseDatabase.reference("shops/$shopId")
        shopRef.child("last_ticket").setValue(0)
        
        val currentServingSnap = shopRef.child("current_servings").valueEvents.first()
        currentServingSnap.children.forEach { child ->
            child.key?.let { cat ->
                shopRef.child("current_servings/$cat").setValue(0)
            }
        }
        firebaseDatabase.reference("active_queues/$shopId").removeValue()
    }

    override suspend fun updateShopInfo(
        shopId: String,
        newName: String,
        newAdText: String,
        newMenuUrl: String,
        newOpenTime: String,
        newCloseTime: String,
        newTheme: String
    ) {
        val shopRef: DatabaseReference = firebaseDatabase.reference("shops/$shopId")
        shopRef.child("name").setValue(newName)
        shopRef.child("ad_text").setValue(newAdText)
        shopRef.child("menu_url").setValue(newMenuUrl)
        shopRef.child("open_time").setValue(newOpenTime)
        shopRef.child("close_time").setValue(newCloseTime)
        shopRef.child("theme").setValue(newTheme)
    }

    override suspend fun updateShopCategories(shopId: String, categories: Map<String, String>) {
        val shopRef = firebaseDatabase.reference("shops/$shopId")
        shopRef.child("categories").setValue(categories)
    }

    override suspend fun getQueueHistory(shopId: String): List<QueueHistoryItem> {
        return withContext(Dispatchers.Default) {
            historyQueries.selectHistoryByShopId(shopId).executeAsList().map { entity ->
                val (_, _, ticketNumber, servedAt) = entity
                QueueHistoryItem(ticketNumber.toInt(), servedAt)
            }
        }
    }

    override suspend fun updateShopStatus(shopId: String, isActive: Boolean) {
        val shopRef = firebaseDatabase.reference("shops/$shopId")
        shopRef.child("is_active").setValue(isActive)
    }

    override suspend fun completeQueue(shopId: String, ticketNumber: Int) {
        val queueRef = firebaseDatabase.reference("active_queues/$shopId/$ticketNumber")
        queueRef.removeValue()
    }

    override suspend fun recallQueue(shopId: String, ticketNumber: Int) {
        val localDateTime = Instant.fromEpochMilliseconds(Clock.System.now().toEpochMilliseconds()).toLocalDateTime(TimeZone.currentSystemDefault())
        val hour = localDateTime.hour.toString().padStart(2, '0')
        val minute = localDateTime.minute.toString().padStart(2, '0')
        val callTimeString = "$hour:$minute"
        val queueRef = firebaseDatabase.reference("active_queues/$shopId/$ticketNumber")
        queueRef.child("call_time").setValue(callTimeString)
    }

    override suspend fun skipQueue(shopId: String, ticketNumber: Int) {
        val queueRef = firebaseDatabase.reference("active_queues/$shopId/$ticketNumber")
        queueRef.child("status").setValue("skipped")
    }

    override suspend fun createNewShop(shopId: String, shopName: String) {
        val shopRef = firebaseDatabase.reference("shops/$shopId")
        val defaultShop = mapOf(
            "id" to shopId,
            "name" to shopName,
            "is_active" to false,
            "last_ticket" to 0,
            "current_servings" to mapOf("A" to 0, "B" to 0, "C" to 0, "D" to 0),
            "updated_at" to Clock.System.now().toEpochMilliseconds().toDouble(),
            "ad_text" to "ยินดีต้อนรับสู่ $shopName",
            "menu_url" to "",
            "open_time" to "08:00",
            "close_time" to "17:00",
            "theme" to "AMBER",
            "categories" to mapOf("A" to "1-2 คน", "B" to "2-3 คน", "C" to "3-4 คน", "D" to "5 คนขึ้นไป")
        )
        shopRef.setValue(defaultShop)
        println("QFAST_MULTI_LOG: 🎉 [สร้างร้านใหม่] -> สร้างร้าน $shopName (ID: $shopId) สำเร็จ")
    }

    override suspend fun deleteShop(shopId: String) {
        firebaseDatabase.reference("shops/$shopId").removeValue()
        firebaseDatabase.reference("active_queues/$shopId").removeValue()
    }

    override suspend fun checkShopExists(shopId: String): Boolean {
        return try {
            val ref = firebaseDatabase.reference("shops/$shopId")
            val snapshot = ref.valueEvents.first()
            println("QFAST_DEBUG: checkShopExists for $shopId exists = ${snapshot.exists}")
            snapshot.exists
        } catch (e: Throwable) {
            println("QFAST_DEBUG: checkShopExists error -> ${e.message}")
            false
        }
    }
}
